/**
 * useMobileNavigation Hook for LOOPINO AI Mobile
 * Handles navigation between mobile screens
 */

import { useState, useCallback, useEffect } from 'react';
import { trackScreenView } from '../utils/analytics';

const normalizeNavigateArgs = (arg) => {
  if (typeof arg === 'boolean') {
    return { addToHistory: arg, source: 'internal' };
  }
  if (typeof arg === 'object' && arg !== null) {
    return {
      addToHistory: arg.addToHistory ?? true,
      source: arg.source ?? 'internal'
    };
  }
  return { addToHistory: true, source: 'internal' };
};

export const useMobileNavigation = (initialScreen = 'posts') => {
  const [currentScreen, setCurrentScreen] = useState(initialScreen);
  const [historyStack, setHistoryStack] = useState([{ screen: initialScreen, source: 'initial' }]);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Available mobile screens
  const screens = {
    posts: 'Post Review',
    calendar: 'Calendario',
    'image-generator': 'AI Images',
    aistorage: 'AI Storage',
    strategy: 'Strategia'
  };

  const navigateToScreen = useCallback((screen, args) => {
    const { addToHistory, source } = normalizeNavigateArgs(args);

    if (screen === currentScreen || isTransitioning) return;

    setIsTransitioning(true);

    if (addToHistory) {
      setHistoryStack(prev => [...prev, { screen, source }]);
    } else {
      setHistoryStack(prev => {
        if (prev.length === 0) {
          return [{ screen, source }];
        }
        const next = [...prev];
        next[next.length - 1] = { screen, source };
        return next;
      });
    }

    setTimeout(() => {
      setCurrentScreen(screen);
      setIsTransitioning(false);
    }, 150);
  }, [currentScreen, isTransitioning]);

  const goBack = useCallback(() => {
    if (historyStack.length <= 1 || isTransitioning) return;

    setIsTransitioning(true);

    setHistoryStack(prev => {
      const next = [...prev];
      next.pop();
      return next;
    });

    const previousEntry = historyStack[historyStack.length - 2];

    setTimeout(() => {
      setCurrentScreen(previousEntry.screen);
      setIsTransitioning(false);
    }, 150);
  }, [historyStack, isTransitioning]);

  const canGoBack = historyStack.length > 1;

  // Navigation functions for each screen
  const navigateToPostReview = useCallback(() => {
    navigateToScreen('posts');
  }, [navigateToScreen]);

  const navigateToCalendar = useCallback(() => {
    navigateToScreen('calendar');
  }, [navigateToScreen]);

  useEffect(() => {
    trackScreenView(`mobile:${currentScreen}`);
  }, [currentScreen]);

  return {
    // Current state
    currentScreen,
    isTransitioning,
    canGoBack,
    screenTitle: screens[currentScreen] || 'Unknown',
    
    // Navigation methods
    navigateToScreen,
    goBack,
    
    // Screen-specific navigators
    navigateToPostReview,
    navigateToCalendar,
    // History
    screenHistory: historyStack.map(entry => entry.screen),
    historyEntries: historyStack,
    clearHistory: () => setHistoryStack([{ screen: currentScreen, source: 'reset' }]),
    
    // Screen checkers
    isPostReviewScreen: currentScreen === 'posts',
    isCalendarScreen: currentScreen === 'calendar'
  };
}; 
